export enum DocumentType {
    Deed = 'deed',
    Insurance = 'insurance',
    Inspection = 'inspection',
    Lease = 'lease',
    Tax = 'tax',
    Mortgage = 'mortgage',
    Permit = 'permit',
    Certificate = 'certificate',
    Invoice = 'invoice',
    /**
     * Rent receipt ("quittance de loyer") — proof that a period WAS paid.
     * Deliberately distinct from `Invoice`, which states a sum still owed: a
     * tenant showing a receipt to a third party is proving the opposite of
     * what an invoice proves, and filing both under one type makes the
     * document browser lie about which is which.
     */
    RentReceipt = 'rent-receipt',
    /**
     * A provider's quote, materialised as a PDF so it can be filed and passed
     * on (#337). Distinct from `Invoice` for the same reason `RentReceipt` is:
     * a quote is a PRICE OFFERED, nothing is owed yet, and filing the two
     * together would make the document browser claim a debt that does not
     * exist.
     *
     * Passing one to a tenant transmits the piece, not the bill: the product
     * states nothing about who ends up paying — that is in the lease, and only
     * the owner has read it.
     */
    Quote = 'quote',
    Contract = 'contract',
    Photo = 'photo',
    PropertyPicture = 'property-picture', // Property photos with multiple size variants
    Other = 'other'
}

/**
 * Image variant metadata for responsive image display
 */
export interface ImageVariant {
    url: string;
    storagePath: string;
    width: number;
    height: number;
    size: number; // File size in bytes
}

/**
 * Container for all image size variants
 */
export interface ImageVariants {
    thumbnail?: ImageVariant;
    small?: ImageVariant;
    medium?: ImageVariant;
    large?: ImageVariant;
    original?: ImageVariant;
}

export class DocumentUpload {
    document_uid?: string;
    type!: DocumentType;
    date!: string;
    name!: string;
    size?: number;
    bucket!: string;
    url!: string;
    downloadUrl!: string;
    fileType!: string;
}


export class Document {
    documentUID: string;
    propertyUID: string;
    type: DocumentType;
    title: string;
    description?: string;
    fileUrl: string;
    fileName: string;
    fileSize: number; // in bytes
    fileType: string; // MIME type
    uploadDate: Date;
    expirationDate?: Date;
    tags?: string[];
    isArchived: boolean;
    
    // Soft delete system with 30-day grace period
    isDeleted?: boolean;
    deletedAt?: Date;
    deletedBy?: string;
    permanentDeletionDate?: Date; // deletedAt + 30 days
    restoredAt?: Date;
    originalStoragePath?: string; // Original path before moving to to_be_deleted
    
    // For version control
    version?: number;
    previousVersionUID?: string;
    
    // For the reusable document system
    entityId?: string; // ID of the entity this document belongs to (property, tenant, contract)
    entityType?: string; // Type of entity (property, tenant, contract)
    
    // Related entity names for better display
    propertyName?: string;
    buildingName?: string;
    tenantName?: string;
    loanName?: string;
    
    // Storage path for easier deletion
    storagePath?: string; // Direct path to the file in Firebase Storage
    
    // Image variants for responsive display (only for PropertyPicture type)
    variants?: ImageVariants;

    // Tenant document sharing (Phase 5 of the tenant workspace effort).
    // When `sharedWithTenant === true`, the backend trigger fans out a
    // read-only projection into each `sharedTenantWorkspaceIds` workspace
    // under `workspaces/{tenantWs}/shared_documents/{documentUID}`.
    sharedWithTenant?: boolean;
    sharedTenantWorkspaceIds?: string[];
    sharedAt?: Date;

    constructor(data: Partial<Document> = {}) {
        this.documentUID = data.documentUID || '';
        this.propertyUID = data.propertyUID || '';
        this.type = data.type || DocumentType.Other;
        this.title = data.title || '';
        this.description = data.description;
        this.fileUrl = data.fileUrl || '';
        this.fileName = data.fileName || '';
        this.fileSize = data.fileSize || 0;
        this.fileType = data.fileType || '';
        this.uploadDate = data.uploadDate || new Date();
        this.expirationDate = data.expirationDate;
        this.tags = data.tags || [];
        this.isArchived = data.isArchived || false;
        this.isDeleted = data.isDeleted || false;
        this.deletedAt = data.deletedAt;
        this.deletedBy = data.deletedBy;
        this.permanentDeletionDate = data.permanentDeletionDate;
        this.restoredAt = data.restoredAt;
        this.originalStoragePath = data.originalStoragePath;
        this.version = data.version || 1;
        this.previousVersionUID = data.previousVersionUID;
        this.entityId = data.entityId;
        this.entityType = data.entityType;
        this.storagePath = data.storagePath;
        this.propertyName = data.propertyName;
        this.buildingName = data.buildingName;
        this.tenantName = data.tenantName;
        this.loanName = data.loanName;
        this.variants = data.variants;
        this.sharedWithTenant = data.sharedWithTenant;
        this.sharedTenantWorkspaceIds = data.sharedTenantWorkspaceIds;
        this.sharedAt = data.sharedAt;
    }
}